import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Building2, CheckCircle2, Plus, MapPin, DollarSign, FileText, Mail, Users, ArrowRight, Sparkles, Building, AlertCircle } from 'lucide-react';
import api from '../api';

const PublicJobPostingPage = () => {
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companyOption, setCompanyOption] = useState('EXISTING'); // 'EXISTING' or 'NEW'
  const [selectedCompany, setSelectedCompany] = useState('');
  
  // New Company fields if 'NEW'
  const [newCompanyName, setNewCompanyName] = useState('');
  const [industry, setIndustry] = useState('Technology & AI');
  const [website, setWebsite] = useState('');
  const [companyDesc, setCompanyDesc] = useState('');

  // Job fields
  const [jobTitle, setJobTitle] = useState('');
  const [salaryPackage, setSalaryPackage] = useState('');
  const [location, setLocation] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [targetBatch, setTargetBatch] = useState('All');
  const [contactEmail, setContactEmail] = useState('');

  // Status state
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      // First try via api service, or direct public fetch
      const res = await api.get('/public/companies');
      const list = res.data || [];
      setCompanies(list);
      if (list.length > 0) {
        setSelectedCompany(list[0].name);
      } else {
        setCompanyOption('NEW');
      }
    } catch (err) {
      console.error('Error fetching public companies:', err);
      // Fallback direct fetch if api fails or not configured
      try {
        const fallbackRes = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/public/companies`);
        if (fallbackRes.ok) {
          const list = await fallbackRes.json();
          setCompanies(list || []);
          if (list && list.length > 0) {
            setSelectedCompany(list[0].name);
          } else {
            setCompanyOption('NEW');
          }
        }
      } catch (fbErr) {
        console.error('Fallback fetch error:', fbErr);
      }
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessData(null);

    const finalCompanyName = companyOption === 'EXISTING' ? selectedCompany : newCompanyName;
    if (!finalCompanyName || !finalCompanyName.trim()) {
      setErrorMsg('Please select or enter a corporate partner name.');
      return;
    }
    if (!jobTitle || !jobTitle.trim()) {
      setErrorMsg('Please enter a job role title.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        company_name: finalCompanyName.trim(),
        job_name: jobTitle.trim(),
        industry: companyOption === 'NEW' ? industry : (companies.find(c => c.name === selectedCompany)?.industry || 'Technology & AI'),
        description: jobDescription,
        salary_package: salaryPackage,
        location: location,
        website: companyOption === 'NEW' ? website : (companies.find(c => c.name === selectedCompany)?.website || ''),
        contact_email: contactEmail,
        target_batch: targetBatch
      };

      const res = await api.post('/public/post-job', payload);
      setSuccessData(res.data);
      // Reset form fields
      setJobTitle('');
      setSalaryPackage('');
      setLocation('');
      setJobDescription('');
      setContactEmail('');
      if (companyOption === 'NEW') {
        setNewCompanyName('');
        setWebsite('');
        setCompanyDesc('');
      }
      fetchCompanies();
    } catch (err) {
      console.error('Error posting job:', err);
      setErrorMsg(err.response?.data?.detail || 'Failed to post job requirement. Please check fields and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '90vh', background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)', padding: '40px 20px' }}>
      <div style={{ maxWidth: '860px', margin: '0 auto' }}>
        
        {/* Header Section */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: '#e0f2fe', color: '#0284c7', padding: '6px 16px', borderRadius: '24px', fontSize: '0.85rem', fontWeight: '700', marginBottom: '16px', border: '1px solid #bae6fd' }}>
            <Sparkles size={16} /> Corporate Partner & TPO Hiring Portal
          </div>
          <h1 style={{ fontSize: '2.4rem', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0', letterSpacing: '-0.5px' }}>
            Public Job Requirement Posting
          </h1>
          <p style={{ fontSize: '1.05rem', color: '#64748b', margin: '0 auto', maxWidth: '640px', lineHeight: '1.6' }}>
            Submit new campus hiring opportunities directly to our University Training & Placement Office (TPO). Once posted, roles appear instantly on the TPO dashboard for drive scheduling.
          </p>
        </div>

        {/* Success Alert Banner */}
        {successData && (
          <div style={{ background: '#ecfdf5', border: '2px solid #10b981', borderRadius: '16px', padding: '28px', marginBottom: '32px', boxShadow: '0 10px 25px rgba(16, 185, 129, 0.1)', textAlign: 'center' }}>
            <div style={{ width: '60px', height: '60px', background: '#d1fae5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <CheckCircle2 size={36} style={{ color: '#10b981' }} />
            </div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: '#065f46', margin: '0 0 8px 0' }}>
              Requirement Successfully Posted!
            </h3>
            <p style={{ color: '#047857', fontSize: '1.02rem', margin: '0 0 20px 0', fontWeight: '500' }}>
              <strong>{successData.job?.job_name}</strong> for <strong>{successData.company?.name}</strong> has been created and listed directly in the TPO Placement Drives & Companies portal.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setSuccessData(null)}
                style={{ padding: '10px 22px', background: '#ffffff', color: '#10b981', border: '1.5px solid #10b981', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', fontSize: '0.95rem', transition: 'all 0.2s' }}
              >
                + Post Another Job Role
              </button>
              <Link
                to="/tpo-dashboard"
                style={{ padding: '10px 24px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#ffffff', borderRadius: '10px', fontWeight: '700', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.25)', fontSize: '0.95rem' }}
              >
                View in TPO Dashboard <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        )}

        {/* Error Message */}
        {errorMsg && (
          <div style={{ background: '#fef2f2', border: '1px solid #f87171', color: '#991b1b', padding: '16px', borderRadius: '12px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px', fontWeight: '600' }}>
            <AlertCircle size={22} style={{ flexShrink: 0, color: '#ef4444' }} />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* Main Posting Form Card */}
        <div style={{ background: '#ffffff', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.04)', overflow: 'hidden' }}>
          
          <div style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', padding: '24px 32px', color: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: '700', color: '#fff' }}>New Job Role Details</h2>
              <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>All fields will instantly reflect across university recruitment dashboards</span>
            </div>
            <Briefcase size={28} style={{ color: '#38bdf8' }} />
          </div>

          <form onSubmit={handleSubmit} style={{ padding: '32px' }}>
            
            {/* Step 1: Corporate Partner Selection */}
            <div style={{ marginBottom: '32px', padding: '20px', background: '#f8fafc', borderRadius: '14px', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <Building2 size={20} style={{ color: '#0284c7' }} />
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>1. Corporate Partner / Company</h3>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '18px' }}>
                <button
                  type="button"
                  onClick={() => setCompanyOption('EXISTING')}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: '10px',
                    border: companyOption === 'EXISTING' ? '2px solid #0284c7' : '1px solid #cbd5e1',
                    background: companyOption === 'EXISTING' ? '#e0f2fe' : '#ffffff',
                    color: companyOption === 'EXISTING' ? '#0284c7' : '#475569',
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  Select Existing Partner ({companies.length})
                </button>
                <button
                  type="button"
                  onClick={() => setCompanyOption('NEW')}
                  style={{
                    flex: 1,
                    padding: '11px',
                    borderRadius: '10px',
                    border: companyOption === 'NEW' ? '2px solid #0284c7' : '1px solid #cbd5e1',
                    background: companyOption === 'NEW' ? '#e0f2fe' : '#ffffff',
                    color: companyOption === 'NEW' ? '#0284c7' : '#475569',
                    fontWeight: '700',
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <Plus size={16} /> Onboard New Partner
                </button>
              </div>

              {companyOption === 'EXISTING' ? (
                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Select Corporate Partner *
                  </label>
                  {loadingCompanies ? (
                    <div style={{ padding: '12px', color: '#64748b' }}>Loading companies...</div>
                  ) : (
                    <select
                      value={selectedCompany}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                      required
                      style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#ffffff', fontSize: '0.95rem', fontWeight: '600', color: '#1e293b' }}
                    >
                      <option value="">-- Choose Company --</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.name}>
                          {c.name} ({c.industry || 'Tech'}) - {c.jobs_count || 0} active roles
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                      New Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. Acme Technologies India Pvt Ltd"
                      value={newCompanyName}
                      onChange={(e) => setNewCompanyName(e.target.value)}
                      style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                      Industry Category
                    </label>
                    <input
                      type="text"
                      placeholder="e.g. Fintech, AI, Semiconductor"
                      value={industry}
                      onChange={(e) => setIndustry(e.target.value)}
                      style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                      Company Website
                    </label>
                    <input
                      type="url"
                      placeholder="https://..."
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Role Details */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                <FileText size={20} style={{ color: '#0284c7' }} />
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>2. Job Role & Compensation</h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
                    Job Role / Designation Title *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Software Development Engineer (SDE-I), Cloud Security Engineer"
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '1rem', fontWeight: '600', color: '#0f172a' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    <DollarSign size={16} style={{ color: '#10b981' }} /> Salary Package / CTC
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. ₹18,000,000 - ₹24,000,000 LPA or $120k/yr"
                    value={salaryPackage}
                    onChange={(e) => setSalaryPackage(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    <MapPin size={16} style={{ color: '#ef4444' }} /> Job Location / Work Mode
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Bengaluru (Hybrid), Hyderabad, Remote"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                  />
                </div>
              </div>
            </div>

            {/* Step 3: Requirements & Eligibility */}
            <div style={{ marginBottom: '36px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
                <Users size={20} style={{ color: '#0284c7' }} />
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#1e293b' }}>3. Eligibility & Description</h3>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '0.9rem', fontWeight: '700', color: '#1e293b', marginBottom: '6px' }}>
                  Job Description, Key Responsibilities & Requirements
                </label>
                <textarea
                  rows={5}
                  placeholder="Describe the day-to-day responsibilities, required programming languages/skills, and educational qualifications..."
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  style={{ width: '100%', padding: '14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem', lineHeight: '1.5', resize: 'vertical' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.88rem', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    Target Graduating Batches
                  </label>
                  <select
                    value={targetBatch}
                    onChange={(e) => setTargetBatch(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', background: '#ffffff', fontSize: '0.95rem', fontWeight: '600' }}
                  >
                    <option value="All">All Eligible Batches</option>
                    <option value="2026">2026 Batch Only</option>
                    <option value="2025">2025 Batch Only</option>
                    <option value="2024">2024 Batch Only</option>
                    <option value="2025, 2026">2025 & 2026 Batches</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.88rem', fontWeight: '600', color: '#334155', marginBottom: '6px' }}>
                    <Mail size={16} style={{ color: '#0284c7' }} /> Recruiter / Contact Email *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="e.g. hr@company.com"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    style={{ width: '100%', padding: '12px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '0.95rem' }}
                  />
                </div>
              </div>
            </div>

            {/* Submit Bar */}
            <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Link to="/tpo-dashboard" style={{ color: '#64748b', textDecoration: 'none', fontWeight: '600', fontSize: '0.92rem' }}>
                ← Return to TPO Portal
              </Link>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  padding: '14px 32px',
                  background: submitting ? '#94a3b8' : 'linear-gradient(135deg, #0284c7, #2563eb)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: '700',
                  fontSize: '1.05rem',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 14px rgba(2, 132, 199, 0.35)',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
              >
                {submitting ? 'Posting Requirement...' : 'Publish Job Requirement to TPO Dashboard'}
                {!submitting && <ArrowRight size={20} />}
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  );
};

export default PublicJobPostingPage;
